import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, set } from '@ember/object';
import { task, timeout } from 'ember-concurrency';
import { createPopper } from '@popperjs/core';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div ...attributes {{did-update this.reloadMap @relatedBelongsTo}}>\n  {{#if this.showRelatedBelongsTo}}\n    <BbChip\n      @applicationController={{@applicationController}}\n      @model={{@relatedModelType}}\n      @item={{this.storeItem}}\n      @openDropFn={{this.openDropTask}}\n      @onRemove={{if this.isEditable this.removeRelated}}\n      @removeBusy={{this.removeRelatedTask.isRunning}}\n    />\n  {{else}}\n    {{#if this.isEditable}}\n      <div class=\'grid-x\'>\n        <div class=\'cell medium-12\'>\n          <SelectAssociation\n            @applicationController={{@applicationController}}\n            @relatedModelType={{@relatedModelType}}\n            @action={{this.addRelated}}\n            data-test-belongs-to-add-related\n            @addNew={{@addNew}}\n            @showOnlyField={{@showOnlyField}}\n            @wrapLabelSearchInput={{this.labelSearchInput}}\n            @completeNew={{@completeNew}}\n            @defaultStatusName={{@defaultStatusName}}\n            @showThumbnail={{@showSearchThumbnail}}\n            @status={{this.defStatus}}\n            @appendQuery={{@appendQuery}}\n            @searchValue={{this.searchValue}}\n            @showRelated={{@showRelated}}\n            @filter_by_contact={{@filter_by_contact}}\n            @filter_by_product={{@filter_by_product}}\n            @filter_by_project={{@filter_by_project}}\n            @updateModelAfterSave={{@updateModelAfterSave}}\n            @modelToUpdate={{@item}}\n          />\n        </div>\n      </div>\n    {{/if}}\n  {{/if}}\n</div>");

var _dec, _dec2, _dec3, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6;
let BelongsToComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  drop: true
}), _dec3 = task({
  drop: true
}), _class = class BelongsToComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "session", _descriptor3, this);
    // undefined = no local override (the @relatedBelongsTo arg wins);
    // null = explicitly cleared via removeRelated. A plain `??` fallback would
    // coalesce the cleared null straight back to the arg, making remove a no-op.
    _initializerDefineProperty(this, "_internalRelated", _descriptor4, this);
    _initializerDefineProperty(this, "searchValue", _descriptor5, this);
    _initializerDefineProperty(this, "refreshModel", _descriptor6, this);
  }
  get relatedBelongsTo() {
    if (this._internalRelated !== undefined) {
      return this._internalRelated;
    }
    return this.args.relatedBelongsTo ?? null;
  }
  set relatedBelongsTo(value) {
    this._internalRelated = value;
  }
  get labelSearchInput() {
    return this.args.wrapLabelSearchInput ?? this.intl.t('search...');
  }
  get saveRelation() {
    return this.args.saveRelation === true ? true : false;
  }
  get defStatus() {
    if (this.args.status) {
      return this.args.status;
    }
    const defStatusName = this.args.defaultStatusName;
    const optionField = this.session.option_field;
    const defStatus = optionField?.def_status?.[defStatusName];
    if (defStatus) {
      const defStatusId = `${defStatus}`;
      const defStatusArray = this.session.me?.organization?.[`${this.args.relatedModelType}_states`];
      return defStatusArray?.find(i => i.id === defStatusId) || null;
    }
    return null;
  }
  get showRelatedBelongsTo() {
    const showRelated = this.args.showRelated ?? true;
    return !!this.storeItem && showRelated;
  }
  get isEditable() {
    return this.args.isEditable === false ? false : true;
  }
  get storeItem() {
    if (this.relatedBelongsTo?.id) {
      return this.store.peekRecord(this.args.relatedModelType, this.relatedBelongsTo.id);
    }
    return null;
  }

  // Task to open dropdown popper
  *openDropTask(item, chipId) {
    const refEl = document.querySelector(`#${chipId}`);
    const popEl = document.querySelector('#zfprevChip');

    // The preview popper is driven entirely through applicationController.prevChip;
    // without it, set() below would assert on undefined.
    // silent-guard-ok: positioning lifecycle, not a user refusal
    if (!popEl || !refEl || !this.args.applicationController) return;
    const prevChip = this.args.applicationController.prevChip;
    if (popEl.classList.contains('is-open') && prevChip?.relDivId === chipId) {
      this.args.applicationController?.zfprevChip?.close();
      return;
    }

    // [regression-fix] model and id were present in Classic (via chip-item standalone path)
    // but dropped when openDropFn was introduced; setPreviewTask guards on prevChip.id
    // and uses prevChip.model for store.findRecord — without them preview/navigation silently no-ops
    set(this.args.applicationController, 'prevChip', {
      chipLabel: item.label ?? item.get?.('label'),
      chipSubLabel: item.sub_label ?? item.get?.('sub_label'),
      chipStatus: item.status_name ?? item.get?.('status_name'),
      isEditable: this.isEditable,
      removeRelated: this.removeRelated,
      item,
      model: this.args.relatedModelType,
      id: item.id,
      _this: this,
      relDivId: chipId
    });
    yield timeout(50);
    if (this.isDestroying || this.isDestroyed) return;
    popEl.classList.add('is-open');
    document.body.classList.add('dropdown-open');
    this._popperInstance?.destroy();
    this._popperInstance = createPopper(refEl, popEl, {
      placement: 'bottom',
      modifiers: [{
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'right', 'top', 'left']
        }
      }]
    });
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this.addRelatedTask?.cancelAll?.();
    this.removeRelatedTask?.cancelAll?.();
    if (this.args.applicationController?.prevChip?._this === this) {
      this.args.applicationController.zfprevChip?.close();
    }
    this._popperInstance?.destroy();
    this._popperInstance = null;
  }
  reloadMap() {
    // The @relatedBelongsTo arg changed: drop the local override so the new
    // arg value wins again (undefined, not null — see _internalRelated).
    this._internalRelated = undefined;
  }
  removeRelated() {
    return this.removeRelatedTask.perform();
  }
  *removeRelatedTask() {
    const persisted = yield* this._persistRelation(null);
    if (!persisted) return false;
    this.relatedBelongsTo = null;
    this.refreshModel = !this.refreshModel;
    if (typeof this.args.action === 'function') {
      this.args.action(null);
    }

    // Only a confirmed unlink dismisses the preview panel — after a refusal the
    // user is left looking at the chip they tried to unlink, and can retry.
    this.args.applicationController?.zfprevChip?.close();
    return true;
  }
  addRelated(id) {
    const item = this.store.peekRecord(this.args.relatedModelType, id);
    if (!item) {
      return false;
    }
    return this.addRelatedTask.perform(item, id);
  }
  *addRelatedTask(item, id) {
    const persisted = yield* this._persistRelation(item);
    if (!persisted) return false;
    this.relatedBelongsTo = item;
    this.refreshModel = !this.refreshModel;
    if (typeof this.args.action === 'function') {
      this.args.action(id);
    }
    return true;
  }

  /**
   * Puts `value` on the host field and, in @saveRelation mode, waits for the
   * server before anything else happens. Resolves true when the relation is
   * the server's, false when it is not.
   *
   * The host field has to carry the value BEFORE the request: `saveRecord`
   * PATCHes the host record, so the field IS the payload — there is no
   * save-first ordering available that still sends the relation. What CAN wait
   * is every consequence an observer sees: the component's own chip and the
   * host @action fire only on a confirmed write.
   *
   * A refusal is undone by hand because `saveRecord` rolls back the record's
   * dirty ATTRIBUTES and a belongsTo is not an attribute — ember-data would
   * leave the refused relation sitting on the record, and the next save of
   * that record would quietly ship it.
   */
  *_persistRelation(value) {
    const previous = this.relatedBelongsTo;
    this.args.setContent?.(value);
    if (!(this.saveRelation && this.args.item?.id)) {
      return true;
    }
    this.args.item.set('my_dirty', !this.args.item.my_dirty);
    // saveRecord NEVER rejects: on refusal it resolves `false`. A try/catch
    // here would be dead code.
    const saved = yield this.args.applicationController.saveRecord(this.args.item);
    if (saved === false) {
      this.args.setContent?.(previous);
      return false;
    }
    return true;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "_internalRelated", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return undefined;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "searchValue", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "refreshModel", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "openDropTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "openDropTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reloadMap", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reloadMap"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeRelated", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeRelated"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeRelatedTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "removeRelatedTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addRelated", [action], Object.getOwnPropertyDescriptor(_class.prototype, "addRelated"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addRelatedTask", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "addRelatedTask"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BelongsToComponent);

export { BelongsToComponent as default };
//# sourceMappingURL=belongs-to.js.map
